<script>
export default {
	name: 'MobileSubpage',
	inheritAttrs: false,
};
</script>

<script setup>
import Anchor from '~/logic/helpers/Anchor.js';
import { useEventBus } from '@vueuse/core';
import usePersistencyWithExpiry from '~/logic/composables/usePersistencyWithExpiry.js';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { useRouter } from 'vue-router';

const props = defineProps({
	isVisible: { type: Boolean, default: false },
	backButtonLabel: { type: String, default: null },
	mobileSubPageDesign: { type: String, default: 'MH' },
	enableGlassDesign: { type: Boolean, default: false },
	blendedHeaderCss: { type: String, default: null },
	mobileSwiperTabs: { type: Array, default: () => [] },
});
const emit = defineEmits([
	'update:isVisible',
]);

const onRootMounted = () => {
	Anchor.activate('body');
};
const mobileSearches = ref([]);
usePersistencyWithExpiry('mobileSearches', {
	refs: {
		mobileSearches,
	},
});
const router = useRouter();
const closeMobileView = () => {
	emit('update:isVisible', false);
	mobileSearches.value = [];
	router.replace({ hash: '' });
	bookingWidgetBus.emit('close-from-mobile', { 'mobileEvent': true });
};

const bookingWidgetBus = useEventBus('booking-widget');

const siteName = window.siteName;

</script>

<template>
<Teleport
	v-if="props.isVisible"
	to="#booking-subpage-container"
	class="MobileSubpage text-white"
	v-bind="$attrs"
>
	<section
		v-if="!props.enableGlassDesign"
		class="section-banner"
		:data-use-theme="siteName"
		:data-use-design="props.mobileSubPageDesign"
		@vue:mounted="onRootMounted"
	>
		<div class="generic-container">
			<button
				class="btn-back flex items-center text-white gap-2"
				type="button"
				@click="emit('update:isVisible', false)"
			>
				<icon-far-chevron-left class="fill-current transform rtl:-rotate-180" />
				<span class="">{{ backButtonLabel ?? $t('Back') }}</span>
			</button>
			
			<div class="my-10 text-white">
				<slot name="banner-title"></slot>
			</div>
		</div>
	</section>

	<section
		v-else
		class="section-banner glass-design"
		:class="blendedHeaderCss"
		:data-use-theme="siteName"
		:data-use-design="props.mobileSubPageDesign"
		@vue:mounted="onRootMounted"
	>
		<div class="generic-container">
			
			<div class="my-2 text-white relative flex items-center justify-center">
				<div class="flex flex-col gap-5">
					<slot name="banner-title"></slot>
					<slot name="swiper"></slot>
				</div>

				<button
					class="cross-icon right-5 w-8 h-8 p-0 md:h-6 md:w-6 flex items-center justify-center text-white"
					type="button"
					@click="closeMobileView()"
				>
					<icon-far-xmark class="w-6 fill-white stroke-[5.5]" />
				</button>

			</div>
		</div>
	</section>
	<div v-if="!props.enableGlassDesign" class="top-rounded-section-breaker"></div>
	
	<slot></slot>
</Teleport>
</template>


<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.MobileSubpage {
	.glass-design {
		.generic-container {
			max-height: auto;
		}
	}
}

.section-banner {
	--batik-bg-colour: var(--primary-blue-base);
	--batik-pattern-img: url('~/assets/batik-pattern.svg');
	/* --batik-pattern-img: url('~/assets/batik-pattern-brighter.svg'); */
	background-color: var(--batik-bg-colour);
	background-image: var(--batik-pattern-img);
	background-size: 200px auto;
	background-repeat: repeat;
	height: 317px;
	overflow: hidden;

	&[data-use-design="glass-design"] {
		// h1 { width: 100%; }

		box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
		background: linear-gradient(to bottom, #0D4689 0%, #0D4689 88%, #BFC5C4 100%);
		height: auto;
		min-height: 146px;
		// max-height: 115px;
		:deep(.SwitchTabHeader) {
			.swiper {
				top: -35px !important;

				.SwitchTabHeaderItem {
					height: 3rem;
				}
			}

			.SwitchTabHeaderItemCenter {
				background-color: var(--bg-glass-swiper);
				color: white;
			}
		}

		:deep(.SwitchTabArrow-wrapper) {
			top: -75px;
			opacity: none;
		}

		:deep(.SwitchTabContent) {
			&.glass-design {
				border-radius: 0px;
			}
		}

		.top-rounded-section-breaker {
			border-top-left-radius: none !important;
			border-top-right-radius: none !important;
		}

		.cross-icon {
			position: absolute;
			right: 22px;
			top: 12px;
		}

		&.blended-header-css {
			// height: 165px;
			// padding-top: 55px;
		}
	}
}

.btn-back {
	margin-top: var(--headerHeight);
}

.top-rounded-section-breaker {
	border-top-left-radius: 24px;
	border-top-right-radius: 24px;
	--bgColor: white;
	background-color: var(--bgColor);
	height: 45px;
	margin-top: -44px;
}

.section-banner[data-use-theme="MHH"] {
	--batik-bg-colour: var(--primary-mhh-teal-base);
	--batik-pattern-img: url('~/assets/floral-pattern-short-banner.svg');
	background-size: cover;
	background-position-y: 45px;
	background-repeat: no-repeat;
}

.section-banner[data-use-theme="firefly"] {
	--batik-bg-colour: var(--primary-firefly-orange-base);
	--batik-pattern-img: '';

	& + .top-rounded-section-breaker {
		height: 56px;
		margin-top: -55px;
	}
}

</style>
